import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Stack,
  Link,
  IconButton,
  Divider,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Twitter as TwitterIcon,
  LinkedIn as LinkedInIcon,
  GitHub as GitHubIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import TyvraLogo from '../components/TyvraLogo';

const Footer: React.FC = () => {
  const theme = useTheme();
  const currentYear = new Date().getFullYear();

  const footerSections = [
    {
      title: 'Product',
      links: [
        { label: 'Features', href: '#features' },
        { label: 'Pricing', href: '#pricing' },
        { label: 'Demo', href: '#demo' },
        { label: 'Integrations', href: '#integrations' },
        { label: 'Changelog', href: '#changelog' },
      ],
    },
    {
      title: 'Resources',
      links: [
        { label: 'Documentation', href: '#docs' },
        { label: 'API Reference', href: '#api' },
        { label: 'Blog', href: '#blog' },
        { label: 'Community', href: '#community' },
        { label: 'Support', href: '#support' },
      ],
    },
    {
      title: 'Company',
      links: [
        { label: 'About Us', href: '#about' },
        { label: 'Careers', href: '#careers' },
        { label: 'Contact', href: '#contact' },
        { label: 'Partners', href: '#partners' },
        { label: 'Press Kit', href: '#press' },
      ],
    },
    {
      title: 'Legal',
      links: [
        { label: 'Privacy Policy', href: '#privacy' },
        { label: 'Terms of Service', href: '#terms' },
        { label: 'Security', href: '#security' },
        { label: 'Cookie Policy', href: '#cookies' },
        { label: 'GDPR', href: '#gdpr' },
      ],
    },
  ];

  return (
    <Box
      component="footer"
      sx={{
        bgcolor: theme.palette.primary.dark,
        color: 'white',
        pt: { xs: 6, md: 10 },
        pb: 4,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background Pattern */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          opacity: 0.03,
          backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
          backgroundSize: '32px 32px',
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Main Footer Content */}
        <Grid container spacing={4} sx={{ mb: 6 }}>
          {/* Brand Section */}
          <Grid item xs={12} md={4}>
            <Stack spacing={3}>
              {/* Logo/Brand */}
              <TyvraLogo variant="footer" />

              {/* Tagline */}
              <Typography
                variant="body2"
                sx={{
                  color: alpha('#ffffff', 0.8),
                  lineHeight: 1.7,
                  maxWidth: '300px',
                }}
              >
                Modern project management with AI-powered estimation and real-time
                collaboration. Built for teams that ship.
              </Typography>

              {/* Social Media Icons */}
              <Stack direction="row" spacing={1}>
                <IconButton
                  sx={{
                    color: alpha('#ffffff', 0.8),
                    bgcolor: alpha('#ffffff', 0.1),
                    '&:hover': {
                      bgcolor: alpha('#ffffff', 0.2),
                      color: 'white',
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  <TwitterIcon />
                </IconButton>
                <IconButton
                  sx={{
                    color: alpha('#ffffff', 0.8),
                    bgcolor: alpha('#ffffff', 0.1),
                    '&:hover': {
                      bgcolor: alpha('#ffffff', 0.2),
                      color: 'white',
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  <LinkedInIcon />
                </IconButton>
                <IconButton
                  sx={{
                    color: alpha('#ffffff', 0.8),
                    bgcolor: alpha('#ffffff', 0.1),
                    '&:hover': {
                      bgcolor: alpha('#ffffff', 0.2),
                      color: 'white',
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  <GitHubIcon />
                </IconButton>
                <IconButton
                  sx={{
                    color: alpha('#ffffff', 0.8),
                    bgcolor: alpha('#ffffff', 0.1),
                    '&:hover': {
                      bgcolor: alpha('#ffffff', 0.2),
                      color: 'white',
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  <EmailIcon />
                </IconButton>
              </Stack>
            </Stack>
          </Grid>

          {/* Footer Links */}
          {footerSections.map((section, index) => (
            <Grid item xs={6} sm={3} md={2} key={index}>
              <Stack spacing={2}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    color: 'white',
                    mb: 1,
                  }}
                >
                  {section.title}
                </Typography>
                {section.links.map((link, linkIndex) => (
                  <Link
                    key={linkIndex}
                    href={link.href}
                    sx={{
                      color: alpha('#ffffff', 0.7),
                      textDecoration: 'none',
                      fontSize: '0.875rem',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        color: 'white',
                        transform: 'translateX(4px)',
                      },
                    }}
                  >
                    {link.label}
                  </Link>
                ))}
              </Stack>
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ borderColor: alpha('#ffffff', 0.1), my: 4 }} />

        {/* Bottom Bar */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
        >
          <Typography
            variant="body2"
            sx={{
              color: alpha('#ffffff', 0.6),
              textAlign: { xs: 'center', sm: 'left' },
            }}
          >
            © {currentYear} TYVRA. All rights reserved.
          </Typography>

          <Stack direction="row" spacing={3}>
            <Link
              href="#privacy"
              sx={{
                color: alpha('#ffffff', 0.6),
                textDecoration: 'none',
                fontSize: '0.875rem',
                transition: 'color 0.3s ease',
                '&:hover': {
                  color: 'white',
                },
              }}
            >
              Privacy
            </Link>
            <Link
              href="#terms"
              sx={{
                color: alpha('#ffffff', 0.6),
                textDecoration: 'none',
                fontSize: '0.875rem',
                transition: 'color 0.3s ease',
                '&:hover': {
                  color: 'white',
                },
              }}
            >
              Terms
            </Link>
            <Link
              href="#security"
              sx={{
                color: alpha('#ffffff', 0.6),
                textDecoration: 'none',
                fontSize: '0.875rem',
                transition: 'color 0.3s ease',
                '&:hover': {
                  color: 'white',
                },
              }}
            >
              Security
            </Link>
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
};

export default Footer;

